import { Component, Injector, ViewChild, ElementRef } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { LiquidacaoRestoService } from '../service/liquidacao-resto.service';
import { MessageService } from 'primeng/api';
import { Validators } from '@angular/forms';
import { RetencaoRestoService } from '../service/retencao-resto.service';
import { EmpenhoRestoService } from '../../empenho-resto/service/empenho-resto.service';
import {
  BaseResourceFormComponent, LiquidacaoResto, LoginContabil,
  EddyAutoComplete, EmpenhoResto, RetencaoResto, GlobalService,
  FuncaoService, DateFormatPipe, Mensagem
} from 'eddydata-lib';

@Component({
  selector: 'app-liquidacao-resto-form',
  templateUrl: './liquidacao-resto-form.component.html'
})
export class LiquidacaoRestoFormComponent extends BaseResourceFormComponent<LiquidacaoResto, LoginContabil> {

  /**
   * Declaração de variáveis
   */
  @ViewChild('numero_empenho') inputField: ElementRef;

  public especie: string;
  public listaUfs: Array<any>;
  public empenhoAutoComplete: EddyAutoComplete<EmpenhoResto>;
  public empenhado: number;
  public liquidado: number;
  public pago: number;
  public listaRetencoes: Array<RetencaoResto>;
  public empenhoNumero: number;
  public empenhoAno: number;
  public redirecionar = false;

  /**
   * Construtor com as injeções de dependencias
   */
  constructor(
    protected injector: Injector,
    protected globalService: GlobalService,
    protected funcaoService: FuncaoService,
    protected messageService: MessageService,
    protected empenhoService: EmpenhoRestoService,
    protected retencaoService: RetencaoRestoService,
    protected liquidacaoService: LiquidacaoRestoService) {
    super(new LiquidacaoResto(), injector, LiquidacaoResto.converteJson, liquidacaoService);
  }

  // ========================================================================
  //                        MÉTODOS ABSTRAÍDOS
  // ========================================================================

  protected criarCamposForm(): void {
    this.entidadeForm = this.fb.group({
      id: [null],
      parcela: [null, [Validators.required]],
      valor_liquidado: [null, [Validators.required]],
      data_liquidacao: [null, [Validators.required]],
      data_vencimento: [null, [Validators.required]],
      historico: [null, [Validators.required, Validators.minLength(2)]],
      mes: [null],
      documento: [null, [Validators.required]],
      anulacao: [false, [Validators.required]],
      impresso: [false, [Validators.required]],
      recolhimento_encargo: [false, [Validators.required]],
      empenho: [null, [Validators.required]],
      aux: [0, [Validators.required]],
      orgao: [this.login.orgao, [Validators.required]],
      exercicio: [this.login.exercicio, [Validators.required]],
      retencoes: [null],
    });
  }

  protected parametrosExtras(): {} {
    return { relations: 'empenho,empenho.favorecido,empenho.orgao,exercicio,orgao,orgao.cidade' };
  }

  protected afterInit(): void {
    this.listaUfs = this.globalService.listaEstados();
    this.carregarAutoCompletes();
    this.inicializaVariavel();
  }

  protected campoFoco(): ElementRef {
    return this.inputField;
  }

  protected beforeSubmit() {
    try {
      if (!this.entidadeForm.get('empenho').value) {
        throw new Error('Informe o empenho para liquidação');
      }
      if (!this.entidadeForm.get('exercicio').value) {
        throw new Error('Informe o exercício da liquidação');
      }
      if (!this.entidadeForm.get('orgao').value) {
        throw new Error('Informe o órgão da liquidação');
      }
      if (!this.entidadeForm.get('historico').value) {
        throw new Error('Informe o histórico da liquidação');
      }
      if (!this.entidadeForm.get('valor_liquidado').value) {
        throw new Error('Informe o valor da liquidação');
      }
      if (!this.entidadeForm.get('data_liquidacao').value) {
        throw new Error('Informe a data da liquidação');
      }
      if (!this.entidadeForm.get('data_vencimento').value) {
        throw new Error('Informe o vencimento da liquidação');
      }
      if (!this.entidadeForm.get('documento').value) {
        throw new Error('Informe o documento da liquidação');
      }

      if (this.entidadeForm.get('valor_liquidado').value <= 0) {
        throw new Error('O valor da liquidação deve ser maior que zero');
      }
      const dtLiquidacao: Date = this.entidadeForm.get('data_liquidacao').value;
      if (dtLiquidacao.getFullYear() !== this.login.exercicio.ano) {
        throw new Error('O ano da data da liquidação está diferente do exercício logado');
      }

      if (this.empenhado - this.liquidado + this.entidade.valor_liquidado - this.entidadeForm.get('valor_liquidado').value < 0) {
        throw new Error('Não há saldo para continuar a liquidação, verifique!');
      }

      this.entidadeForm.get('mes').setValue(dtLiquidacao.getMonth());
      this.entidadeForm.get('retencoes').setValue(this.listaRetencoes);
      const dataLiquidacao = new DateFormatPipe().transform(this.entidadeForm.get('data_liquidacao').value, []);
      this.entidadeForm.get('mes').setValue(dataLiquidacao.getUTCMonth() + 1);
    } catch (e) {
      this.messageService.add({ severity: 'error', summary: 'Validação', detail: e });
      throw e;
    }
  }

  protected afterSubmit(ent: LiquidacaoResto) {
    this.listaRetencoes = new Array<RetencaoResto>();

    if (this.currentActionRoute === 'novo') {
      if (this.login.redirecionar_liquidar_pagar && this.podeIncluir('/pagamentos-resto')) {
        this.router.navigate(['/pagamentos-resto', 'novo', ent.id]);
      } else if (!this.limparTela) {
        this.router.navigate(['/liquidacoes-restos-pagar', ent.id, 'editar']);
      } else {
        this.router.navigate(['/liquidacoes-restos-pagar', 'novo']);
      }
      this.skipRedirect = false;
    }
  }

  protected afterLoad() {
    this.loadTotalizadores().then(() => {
      this.retencaoService.obterPorLiquidacao(this.entidade.id).pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (data: any) => {
            this.listaRetencoes = data ? data.content : new Array<RetencaoResto>();
          }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
        );
    });
    this.empenhoNumero = this.entidade.empenho.numero;
    this.empenhoAno = this.entidade.empenho.ano;
    this.entidade.data_liquidacao = new DateFormatPipe().transform(this.entidade.data_liquidacao, []);
    this.entidade.data_vencimento = new DateFormatPipe().transform(this.entidade.data_vencimento, []);
  }

  // ========================================================================
  //                            MÉTODOS DA CLASSE
  // ========================================================================

  private inicializaVariavel() {
    if (this.currentActionRoute === 'novo') {
      this.listaRetencoes = new Array<RetencaoResto>();

      this.liquidacaoService.ultimaDataLiquidada(this.login.exercicio.id, this.login.orgao.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((dataLiq) => {
          if (dataLiq === 0) {
            this.entidade.data_liquidacao = new Date();
          } else {
            this.entidade.data_liquidacao = new DateFormatPipe().transform(dataLiq, []);
          }
          this.entidadeForm.get('data_liquidacao').setValue(this.entidade.data_liquidacao);
          this.entidadeForm.get('mes').setValue(this.entidade.data_liquidacao.getUTCMonth() + 1);
        });

      this.activatedRoute.params.pipe(takeUntil(this.unsubscribe))
        .subscribe(
          (params: any) => {
            if (params.hasOwnProperty('empenho')) {
              const parametros = {};
              parametros['id'] = params['empenho'];
              parametros['relations'] = 'exercicio,orgao,favorecido,subelemento,ficha,ficha.acao,ficha.despesa,ficha.programa';
              this.empenhoService.obter(parametros)
                .pipe(takeUntil(this.unsubscribe))
                .subscribe(
                  (entidade) => {
                    this.redirecionar = true;
                    this.empenhoNumero = entidade.numero;
                    this.empenhoAno = entidade.ano;
                    this.entidadeForm.get('empenho').setValue(entidade);
                    this.entidadeForm.get('historico').setValue(entidade.historico);
                    this.entidadeForm.get('data_vencimento').setValue(new DateFormatPipe().transform(entidade.data_vencimento, []));
                    this.entidade.empenho = entidade;
                    if (this.entidade.empenho) {
                      this.loadTotalizadores();
                      this.obterUltimaParcela();
                    }
                  }, () => this.sair());
            }
          });
    }
  }

  private carregarAutoCompletes() {
    // autocomplete para empenho
    this.empenhoAutoComplete = new EddyAutoComplete(this.entidadeForm.get('empenho'), this.empenhoService,
      'id', ['numero', 'ano'], { orgao_id: this.login.orgao.id, relations: 'orgao,favorecido', orderBy: 'numero' }, { number: ['numero'] },
      () => {
        this.loadTotalizadores();
      }
    );
  }

  public async atualizarEmpenho() {
    if (this.empenhoNumero > 0 && !this.entidade.id) {
      const param = {};
      param['numero'] = this.empenhoNumero;
      param['ano'] = this.empenhoAno;
      param['orgao.id'] = this.login.orgao.id;
      param['relations'] = 'favorecido';

      this.entidade.empenho = await this.empenhoService.obter(param).pipe(takeUntil(this.unsubscribe)).toPromise();
      if (!this.entidade.empenho) {
        Mensagem.alerta(this.messageService, 'Empenho não localizado!');
        this.entidadeForm.get('empenho').setValue(null);
        this.loadTotalizadores();
        return;
      }

      if (this.entidade.empenho) {
        this.loadTotalizadores();
        this.obterUltimaParcela();
        if (!this.entidadeForm.get('data_liquidacao').value) {
          this.entidadeForm.get('data_liquidacao').setValue(new Date());
        }
        this.entidadeForm.get('empenho').setValue(this.entidade.empenho);
        this.entidadeForm.get('data_vencimento').setValue(new DateFormatPipe().transform(this.entidade.empenho.data_vencimento, []));
        this.entidadeForm.get('historico').setValue(this.entidade.empenho.historico);
        this.entidadeForm.get('mes').setValue(this.entidade.data_liquidacao.getUTCMonth() + 1);
      }
    }
  }

  /* Se o usuario informou um valor diferente do empenhado
   * atribui a parcela como sendo a primeira
   */
  public verificarParcela() {
    if ((+this.empenhado + (this.entidade.id ? +this.entidade.valor_liquidado : 0) - +this.liquidado - +this.entidadeForm.get('valor_liquidado').value) < 0) {
      Mensagem.alerta(this.messageService, 'Não há saldo disponível para esta liquidação!');
    } else {
      this.obterUltimaParcela();
    }
  }

  private async obterUltimaParcela() {
    this.liquidacaoService.obterUltimaParcela(
      this.entidade.empenho.numero, this.entidade.empenho.ano, this.entidade.empenho.orgao.id
    ).pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (res: any) => {
          this.entidade.parcela = res;
          if (+res === 0 &&
            +this.empenhado !== +this.entidadeForm.get('valor_liquidado').value) {
            this.entidadeForm.get('parcela').setValue(1);
          } else {
            this.entidadeForm.get('parcela').setValue(res);
          }
        }, error => this.messageService.add({ severity: 'error', summary: 'Atenção', detail: error })
      );
  }

  private async loadTotalizadores() {
    this.empenhado = 0;
    this.liquidado = 0;
    this.pago = 0;

    if (!this.entidade.empenho) {
      return;
    }

    let totalizadores = await this.empenhoService.totalizadores(
      this.entidade.empenho.ano,       
      this.entidade.empenho.orgao.id, 
      this.entidade.empenho.id).toPromise();

    if (totalizadores) {
      totalizadores = totalizadores.content[0] ? totalizadores.content[0] : totalizadores.content;
      this.empenhado = totalizadores.total_empenhado;
      this.liquidado = totalizadores.total_liquidado;
      this.pago = totalizadores.total_pago;
    }

    if (!this.entidadeForm.get('id').value) {
      const saldo = (+this.empenhado - +this.liquidado);
      this.entidadeForm.get('valor_liquidado').setValue(saldo.toString());
      if (saldo <= 0) {
        Mensagem.alerta(this.messageService, 'Não há saldo disponível para esta liquidação!');
      }
    }
  }

  public voltar() {
    switch (this.login.sistema) {
      case 'contabil':
      case 'controle-interno':
        this.router.navigate(['/contabilidade']);
        break;
      default:
        this.sair();
        break;
    }
  }
}
